import React, { useState } from 'react';
import './GuestPage.css';
import authService from '../services/authService';
import NewRequest from './NewRequest';
import GuestCasesCalendar from './GuestCasesCalendar';
import GuestMeetings from './GuestMeetings';
import NewGuestMeeting from './NewGuestMeeting';
import GuestEditProfile from './GuestEditProfile';
import DirectConversations from './DirectConversations';

const GuestPage = ({ phoneNumber, userData, token, onLogout }) => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [localUserData, setLocalUserData] = useState(userData);

  const handleProfileUpdate = (updatedData) => {
    setLocalUserData(prev => ({
      ...prev,
      ...updatedData
    }));
  };

  const handleLogoutClick = async () => {
    try {
      setIsLoggingOut(true);
      console.log('Logging out guest with phone:', phoneNumber);
      
      // Call logout API
      await authService.logout(phoneNumber);
      console.log('Logout API call successful');
      
      // Call parent logout handler to reset app state
      onLogout();
    } catch (error) {
      console.error('Logout error:', error.message);
      // Still logout locally even if API fails
      onLogout();
    } finally {
      setIsLoggingOut(false);
    }
  };

  const menuItems = [
    {
      icon: '📋',
      title: 'My Cases',
      subtitle: 'Current Running Cases',
      id: 'cases',
    },
    {
      icon: '✏️',
      title: 'New Request',
      subtitle: 'Initiate New Request',
      id: 'new-request',
    },
    {
      icon: '📅',
      title: 'My Meetings',
      subtitle: 'Meetings Scheduled with Clients',
      id: 'meetings',
    },
    {
      icon: '📅',
      title: 'New Meeting',
      subtitle: 'Create new meeting',
      id: 'new-meeting',
    },
    {
      icon: '💰',
      title: 'Transactions',
      subtitle: 'View and manage transactions',
      id: 'transactions',
    },
  ];

  return (
    <div className="guest-container">
      {/* Header */}
      <div className="guest-header">
        <div className="logo-section">
          <div className="logo">
            <span className="logo-icon">⚖️</span>
            <span className="logo-text">INSAFAPP</span>
          </div>
        </div>
        <div className="header-actions">
          <button 
            className="logout-button" 
            onClick={handleLogoutClick}
            disabled={isLoggingOut}
          >
            {isLoggingOut ? 'Logging out…' : 'Logout'}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="guest-content">
        {activeTab === 'home' && (
          <div className="home-view">
            <div className="menu-grid">
              {menuItems.map(item => (
                <div
                  key={item.id}
                  className="menu-card"
                  onClick={() => setActiveTab(item.id)}
                >
                  <div className="menu-icon">{item.icon}</div>
                  <div className="menu-info">
                    <h3 className="menu-title">{item.title}</h3>
                    <p className="menu-subtitle">{item.subtitle}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'cases' && (
          <GuestCasesCalendar 
            token={token}
            onBack={() => setActiveTab('home')}
          />
        )}

        {activeTab === 'new-request' && (
          <NewRequest 
            phoneNumber={phoneNumber}
            token={token}
            onBack={() => setActiveTab('home')}
          />
        )}

        {activeTab === 'meetings' && (
          <GuestMeetings 
            token={token}
            onBack={() => setActiveTab('home')}
          />
        )}

        {activeTab === 'new-meeting' && (
          <NewGuestMeeting
            token={token}
            onBack={() => setActiveTab('home')}
          />
        )}

        {activeTab === 'transactions' && (
          <div className="view-section">
            <h2>Transactions</h2>
            <p>Your transactions will appear here.</p>
            <button 
              className="back-button"
              onClick={() => setActiveTab('home')}
            >
              ← Back
            </button>
          </div>
        )}

        {activeTab === 'direct-chat' && (
          <DirectConversations
            token={token}
            userData={localUserData}
          />
        )}

        {activeTab === 'profile' && (
          <div className="view-section profile-view">
            <h2>My Profile</h2>
            {localUserData ? (
              <div className="profile-card">
                <div className="profile-header">
                  {localUserData.profile_image && (
                    <img 
                      src={localUserData.profile_image.startsWith('http') ? localUserData.profile_image : `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/${localUserData.profile_image}`} 
                      alt="Profile" 
                      className="profile-image"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  {!localUserData.profile_image && (
                    <div className="profile-image-placeholder">👤</div>
                  )}
                  <div className="profile-name">
                    <h3>{localUserData.first_name} {localUserData.last_name}</h3>
                    <p className="profile-role">👤 Guest</p>
                  </div>
                </div>

                <div className="profile-details">
                  <div className="profile-section">
                    <h4>Contact Information</h4>
                    <div className="profile-item">
                      <label>Phone Number:</label>
                      <p>{localUserData.mobile_number}</p>
                    </div>
                  </div>

                  <div className="profile-section">
                    <h4>Location</h4>
                    <div className="profile-item">
                      <label>Country:</label>
                      <p>{localUserData.country}</p>
                    </div>
                    <div className="profile-item">
                      <label>State:</label>
                      <p>{localUserData.state}</p>
                    </div>
                    <div className="profile-item">
                      <label>City:</label>
                      <p>{localUserData.city}</p>
                    </div>
                  </div>

                  <div className="profile-section">
                    <h4>Account Information</h4>
                    <div className="profile-item">
                      <label>Status:</label>
                      <p>{localUserData.status === 0 ? 'Active' : 'Inactive'}</p>
                    </div>
                    <div className="profile-item">
                      <label>Member Since:</label>
                      <p>{new Date(localUserData.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>

                <div className="profile-actions">
                  <button 
                    className="edit-profile-button"
                    onClick={() => setActiveTab('edit-profile')}
                  >
                    ✏️ Edit Profile
                  </button>
                  <button 
                    className="back-button"
                    onClick={() => setActiveTab('home')}
                  >
                    ← Back
                  </button>
                </div>
              </div>
            ) : (
              <div className="no-data">
                <p>Profile data not available. Please log in again.</p>
                <button 
                  className="back-button"
                  onClick={() => setActiveTab('home')}
                >
                  ← Back
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'edit-profile' && (
          <GuestEditProfile
            onBack={() => setActiveTab('profile')}
            token={token}
            userData={localUserData}
            onProfileUpdate={handleProfileUpdate}
          />
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="guest-navbar">
        <button
          className={`nav-button ${activeTab === 'home' ? 'active' : ''}`}
          onClick={() => setActiveTab('home')}
        >
          <span className="nav-icon">🏠</span>
          <span className="nav-label">Home</span>
        </button>
        <button
          className={`nav-button ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          <span className="nav-icon">📋</span>
          <span className="nav-label">Requests</span>
        </button>
        <button
          className={`nav-button ${activeTab === 'direct-chat' ? 'active' : ''}`}
          onClick={() => setActiveTab('direct-chat')}
        >
          <span className="nav-icon">💬</span>
          <span className="nav-label">Chat</span>
        </button>
        <button
          className={`nav-button ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <span className="nav-icon">👤</span>
          <span className="nav-label">Profile</span>
        </button>
      </div>
    </div>
  );
};

export default GuestPage;
